import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }

const STATUS_COLORS = {
  pending:   { color: "var(--amber-light)", bc: "var(--amber)", badge: "warning" },
  sent:      { color: "#f0f7ff",            bc: "#185fa5",     badge: "info" },
  responded: { color: "var(--gl)",          bc: "var(--g)",    badge: "success" },
  paid:      { color: "var(--gl)",          bc: "var(--g)",    badge: "success" },
  legal:     { color: "var(--red-light)",   bc: "var(--red)",  badge: "danger" },
};

const switches = [
  "Relances automatiques activées",
  "Relance préventive J-7",
  "SMS en complément email",
  "Lien de paiement Stripe dans les emails",
  "Pause si paiement partiel détecté",
];

export default function Relances({ currentCompanyId }) {
  const [relances, setRelances] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [autoMsg, setAutoMsg]   = useState("");

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.relances.list({ company_id: currentCompanyId })
      .then(data => setRelances(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleAuto = async () => {
    setRunning(true);
    setAutoMsg("");
    try {
      const res = await api.relances.auto(currentCompanyId);
      setAutoMsg(res.message);
      load();
    } catch (err) { setAutoMsg(err.message); }
    finally { setRunning(false); }
  };

  const handleSend = async (id) => {
    await api.relances.send(id).catch(console.error);
    load();
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  const unpaidTotal = relances.filter(r => !["paid","responded"].includes(r.status)).reduce((s, r) => s + r.amount, 0);
  const sentCount   = relances.filter(r => r.status === "sent").length;
  const paidCount   = relances.filter(r => r.status === "paid").length;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Relances automatiques</h2>
        <div className="btn-group">
          <button className="btn" onClick={handleAuto} disabled={running}>
            {running ? "⏳ Génération…" : "🤖 Générer relances auto"}
          </button>
          <button className="btn primary">+ Relance manuelle</button>
        </div>
      </div>

      {autoMsg && <div className="alert success" style={{ marginBottom: 16 }}><span>✅</span>{autoMsg}</div>}

      <div className="kpi-grid k4">
        <div className="kpi"><div className="kpi-label">Impayés totaux</div><div className="kpi-value" style={{ color: "var(--red)" }}>{fmt(unpaidTotal)} €</div><div className="kpi-sub down">{relances.filter(r => !["paid"].includes(r.status)).length} clients</div></div>
        <div className="kpi"><div className="kpi-label">Relances envoyées</div><div className="kpi-value">{sentCount}</div></div>
        <div className="kpi"><div className="kpi-label">Réglées</div><div className="kpi-value" style={{ color: "var(--g)" }}>{paidCount}</div></div>
        <div className="kpi"><div className="kpi-label">Total relances</div><div className="kpi-value">{relances.length}</div></div>
      </div>

      <div className="g2">
        <div className="card">
          <div className="card-header"><span className="card-title">📋 Factures impayées</span></div>
          {relances.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }}>✅ Aucune relance en cours</div>
          ) : relances.map((r, i) => {
            const s = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
            const levelLabel = r.level === 3 ? "Mise en demeure" : r.level === 2 ? `Relance ${r.level} — Formelle` : `Relance ${r.level} — Amiable`;
            return (
              <div key={r.id} style={{ padding: "12px 14px", background: s.color, borderLeft: `3px solid ${s.bc}`, borderRadius: "0 8px 8px 0", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{r.client_name}</span>
                  <span style={{ fontWeight: 800, fontSize: 14 }}>{fmt(r.amount)} €</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
                  {r.days_overdue > 0 ? `${r.days_overdue}j retard` : "À venir"}
                  {r.invoice_number && <span style={{ marginLeft: 8, fontFamily: "monospace" }}>· {r.invoice_number}</span>}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className={`badge ${s.badge}`}>{levelLabel}</span>
                  {!["paid", "legal"].includes(r.status) && (
                    <button className="btn sm" onClick={() => handleSend(r.id)}>📧 Envoyer</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">⚙️ Paramètres des relances</span></div>
          {switches.map((label, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: i < switches.length - 1 ? "0.5px solid #f3f4f6" : "none" }}>
              <span style={{ fontSize: 12, flex: 1 }}>{label}</span>
              <label className="toggle"><input type="checkbox" defaultChecked={i !== 4} /><div className="toggle-track" /><div className="toggle-thumb" /></label>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">📅 Scénario de relance automatique</span></div>
        <div className="timeline">
          {[
            ["done",   "J-7 avant échéance",  "Email automatique",   "Rappel amical avec lien de paiement sécurisé Stripe"],
            ["active", "J+3 après échéance",  "Email + SMS",         "1ère relance — ton cordial, lien paiement inclus"],
            ["todo",   "J+10 après échéance", "Email semi-auto",     "2ème relance — ton ferme, mention pénalités de retard"],
            ["todo",   "J+20 après échéance", "Mise en demeure AR",  "PDF légal généré, validation manuelle avant envoi"],
          ].map(([status, title, canal, desc]) => (
            <div key={title} className="timeline-item">
              <div className="timeline-left"><div className={`timeline-dot ${status}`} /><div className="timeline-line" /></div>
              <div className="timeline-right">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{title}</span>
                  <span className={`badge ${status === "done" ? "success" : status === "active" ? "info" : "draft"}`}>{canal}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
